import {
	ReactElement,
	useCallback
} from "react";
import {
	canCompressImage,
	compressImageToFitFileSize,
	ImageCompressionResult
} from "client/Misc/imageUtils";
import { useCompressImageDialog } from "./CompressImageDialog";

export function useCompressImageBeforeUpload(
	maxFileSize: number,
	onIsBusyChange: (isBusy: boolean) => void
): [
	element: ReactElement | null,
	resolveFileForUpload: (selectedFile: File) => Promise<File>
] {
	const [compressImageDialog, openCompressImageDialog] = useCompressImageDialog();

	const resolveFileForUpload = useCallback(
		async (selectedFile: File): Promise<File> => {
			if (selectedFile.size <= maxFileSize
				|| !canCompressImage(selectedFile.type)
			) {
				return selectedFile;
			}

			onIsBusyChange(true);

			const compressionResult: ImageCompressionResult = await compressImageToFitFileSize(
				selectedFile,
				maxFileSize
			);

			onIsBusyChange(false);

			if (compressionResult.kind !== "compressed") {
				return selectedFile;
			}

			const { image } = compressionResult;

			const confirmation: TryResult<void> = await openCompressImageDialog({
				originalFile: selectedFile,
				image,
				maxFileSize
			});

			return confirmation.wasSuccessful
				? image.file
				: selectedFile;
		},
		[
			maxFileSize,
			onIsBusyChange,
			openCompressImageDialog
		]
	);

	return [
		compressImageDialog,
		resolveFileForUpload
	];
}
